import { Component, inject, Input, ViewEncapsulation } from '@angular/core';

import { EmailButtonComponent } from '../../components/email-button/email-button.component';
import type { DailyReport } from '../../components/email-candidates-reports/email-candidates-reports.component';
import { EmailCandidatesReportsComponent } from '../../components/email-candidates-reports/email-candidates-reports.component';
import { EmailCheckmarkComponent } from '../../components/email-checkmark/email-checkmark.component';
import { EmailCodeBoxComponent } from '../../components/email-codebox/email-codebox.component';
import type { GDPRReport } from '../../components/email-gdpr-reports/email-gdpr-reports.component';
import { EmailGdprReportsComponent } from '../../components/email-gdpr-reports/email-gdpr-reports.component';
import { EmailInfoboxComponent } from '../../components/email-infobox/email-infobox.component';
import { EmailLayoutComponent } from '../../components/email-layout/email-layout.component';
import { EmailMarginComponent } from '../../components/email-margin/email-margin.component';
import { EmailMessageHeaderComponent } from '../../components/email-message-header/email-message-header.component';
import { EmailRecruiteeLogoComponent } from '../../components/email-recruitee-logo/email-recruitee-logo.component';
import { EmailTextComponent } from '../../components/email-text/email-text.component';
import { SsrMarkToUnwrapDirective } from '../../directives/ssr-markto-unwrap.directive';
import type { WrappedValue } from '../../mailgun/wrap-value';
import { MailgunWrapVariablePipe } from '../../pipes/mailgun-wrap-variable.pipe';
import { TemplateRendererConfig } from '../../providers/template-renderer-config';

@Component({
  selector: 'email-snippet',
  template: `
    <email-text
      [contents]="label | mailgunWrapVariable: 'label' : config"
      presetName="header"
      size="medium"
    />
    <email-margin [height]="18" />
    <ng-content />
    <email-margin [height]="64" />
  `,
  hostDirectives: [SsrMarkToUnwrapDirective],
  imports: [MailgunWrapVariablePipe, EmailTextComponent, EmailMarginComponent],
  encapsulation: ViewEncapsulation.None,
})
export class EmailDemoTemplateSnippetComponent {
  public config = inject(TemplateRendererConfig);
  @Input() public label: WrappedValue<any>;
}

@Component({
  selector: 'email-demo-template',
  styleUrls: ['../../global-styles.less'],
  templateUrl: './demo.component.html',
  imports: [
    // demo only
    EmailDemoTemplateSnippetComponent,
    // generic
    EmailLayoutComponent,
    EmailRecruiteeLogoComponent,
    EmailMessageHeaderComponent,
    EmailButtonComponent,
    EmailInfoboxComponent,
    EmailCodeBoxComponent,
    EmailCandidatesReportsComponent,
    EmailGdprReportsComponent,
    EmailTextComponent,
    EmailCheckmarkComponent,
  ],
  hostDirectives: [SsrMarkToUnwrapDirective],
  encapsulation: ViewEncapsulation.None,
})
export class DemoTemplateComponent {
  @Input({ required: true }) public data: WrappedValue<DemoTemplateData>;
}

export type DemoTemplateData = {
  activation_code: string;
  avatar_url: string;
  header: string;
  subheader: string;
  body: string;
  warning: string;
  footer: string;
  some_link: {
    label: string;
    href: string;
  };
  gdpr_reports: GDPRReport[];
  daily_reports: DailyReport[];
};

export const DemoTemplateMocks: DemoTemplateData[] = [
  {
    activation_code: '12345',
    avatar_url: 'https://ps.w.org/wp-user-avatar/assets/icon-128x128.png?rev=1755722',
    header: 'Hi Jane 👋',
    subheader: 'Olivia Novak invited you to the ACME team in Recruitee.',
    footer: 'Footer',
    some_link: {
      href: 'http://recruitee.com',
      label: 'Recruitee',
    },
    body: 'Foobar',
    warning: `<p>Lorem <b>ipsum</b>b dolor sit amet, consectetur adipiscing elit.
    Cras ac maximus sem. Aenean maximus <a href="#">luctus condimentum</a>.</p><ul><li>Foo</li><li>Bar</li></ul><p>hello</p><ol><li>foo</li><li>bar</li></ol>`,
    gdpr_reports: [
      {
        title: 'Sooner or later some folks will set ridiculously long name, just because they can.',
        period: '24 Aug - 4 Sep',
        items: [
          {
            new: true,
            new_label: 'New',
            title: 'One morning, when Gregor Samsa woke from troubled dreams',
            value: '133 222',
          },
          {
            new: true,
            new_label: 'New',
            title:
              'His many legs, pitifully thin compared with the size of the rest of him, waved about helplessly as he looked.',
            value: 0,
          },
          {
            new: true,
            new_label: 'New',
            title: 'Some title',
            value: 0,
          },
        ],
      },
      {
        title: 'GDPR Report B',
        items: [
          {
            new: true,
            new_label: 'New',
            title: 'Some title',
            value: 1,
          },
          {
            new: true,
            new_label: 'New',
            title:
              'His many legs, pitifully thin compared with the size of the rest of him, waved about helplessly as he looked.',
            value: 1337,
          },
          {
            new: false,
            new_label: 'New',
            title: 'One morning, when Gregor Samsa woke from troubled dreams',
            value: '13 549',
          },
        ],
      },
    ],
    daily_reports: [
      {
        title: 'Sooner or later some folks will set ridiculously long name, just because they can.',
        url: 'http://recruitee.com',
        candidates: [
          {
            avatar_url: 'https://ps.w.org/wp-user-avatar/assets/icon-128x128.png?rev=1755722',
            url: 'http://recruitee.com',
            name: 'John Doe The Third Of Great Bumblebees',
            stage: 'Some stage',
          },
          {
            avatar_url: 'https://ps.w.org/wp-user-avatar/assets/icon-128x128.png?rev=1755722',
            url: 'http://recruitee.com',
            name: 'John doe',
            stage: 'Hit the road Jack',
          },
          {
            avatar_url: 'https://ps.w.org/wp-user-avatar/assets/icon-128x128.png?rev=1755722',
            url: 'http://recruitee.com',
            name: 'John doe',
            stage: 'No more no more no more no more.',
          },
        ],
      },
      {
        title: 'Daily report B',
        url: 'http://recruitee.com',
        candidates: [
          {
            avatar_url: 'https://ps.w.org/wp-user-avatar/assets/icon-128x128.png?rev=1755722',
            url: 'http://recruitee.com',
            name: 'John doe',
            stage: 'Some stage',
          },
        ],
      },
    ],
  },
];
